import React, { useState, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SimpleBar from "simplebar-react";
import { Bot, User, Send, ArrowLeft } from "lucide-react";
import { searchRAG } from 'services/knowledge.service';
import { getCampaignConversations, getCampaignResponse, getDetailEmailCampaign } from 'services/campaign.service';
import DOMPurify from 'dompurify';


interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: string;
  sendFrom: 'User' | 'Agent';
}
const CampaignAgent = () => {
  const { emailCampaignId, mode } = useParams<{ emailCampaignId: string; mode: string }>();
  const location = useLocation();
  const isEditMode = mode === 'edit' || location.pathname.includes('/edit');
  const isViewMode = mode === 'view' || location.pathname.includes('/view');

  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const chatRef = useRef<any>(null);
  const [campaignId, setCampaignId] = useState<any>(emailCampaignId || null);
  const [statusCampaign, setStatusCampaign] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Hello! I am your Campaign Assistant. How can I help you today?',
      sendFrom: 'Agent',
      isUser: false,
      timestamp: new Date().toISOString()
    }
  ]);

  useEffect(() => {
    if (emailCampaignId) {
      loadCampaignConversation(emailCampaignId!);
    }
  }, [emailCampaignId, mode, location.pathname]);

  useEffect(() => {
    if (chatRef.current?.el) {
      chatRef.current.getScrollElement().scrollTop = chatRef.current.getScrollElement().scrollHeight;
    }
  }, [messages]);

  const formatDateTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return '';
    }
  };

  const loadCampaignConversation = async (campaignId: string) => {
    const response = await getCampaignConversations(campaignId);
    if (response) {
      // map response to messages
      const loadedMessages: Message[] = response.map((conv: any) => ({
        id: conv.timestamp.toString(),
        content: conv.content,
        isUser: conv.isUser,
        sendFrom: conv.sendFrom,
        timestamp: conv.timestamp
      }));
      setMessages(prev => [...prev, ...loadedMessages]);
    }
  }

  const loadStatusCampaign = async (campaignId: string) => {
    const response = await getDetailEmailCampaign(campaignId);
    if (response && response?.status?.toString() === 'Sent') {
      setStatusCampaign(response?.status);
    }
  }


  const addMessage = () => {
    if (currentMessage.trim() !== '') {
      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        content: currentMessage,
        isUser: true,
        sendFrom: 'User',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage]);
      setCurrentMessage('');
      setIsTyping(true);

      // Simulate AI response delay
      setTimeout(async () => {
        // Add AI response
        const payload = {
          History: messages,
          campaignId: campaignId,
          LastMessage: currentMessage
        };
        const response = await getCampaignResponse(payload);
        setCampaignId(response.emailCampaignId);
        await loadStatusCampaign(response.emailCampaignId);
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.content,
          isUser: false,
          sendFrom: 'Agent',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiMessage]);
        setIsTyping(false);
      }, 1500 + Math.random() * 1000); // Random delay between 1.5-2.5 seconds
    }
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMessage();
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-slate-50 dark:bg-zink-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-zink-600 bg-white dark:bg-zink-700">
        <div className="flex items-center gap-3">
          <div className='cursor-pointer hover:bg-slate-200 dark:hover:bg-zink-600 rounded-full p-1'
            onClick={() => window.history.back()}>
            <ArrowLeft />
          </div>
          <div className="flex items-center justify-center size-10 bg-custom-100 dark:bg-custom-500/20 rounded-full shrink-0">
            <Bot className="size-5 text-custom-500" />
          </div>
          <div>
            <h6 className="text-base font-semibold text-slate-800 dark:text-zink-50">Campaign Assistant</h6>
            <p className="text-xs text-slate-500 dark:text-zink-300">AI-powered campaign helper</p>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <SimpleBar ref={chatRef} className="h-full px-4 py-4">
          <ul className="flex flex-col gap-4 list-none">
            {messages.map((message) => (
              <li
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-3 max-w-[85%] md:max-w-[75%] ${message.isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Avatar */}
                  <div className="flex-shrink-0 self-end">
                    {message.isUser ? (
                      <div className="bg-slate-200 dark:bg-zink-600 text-slate-500 dark:text-zink-200 rounded-full size-8 flex items-center justify-center">
                        <User className="size-4" />
                      </div>
                    ) : (
                      <div className="bg-custom-100 dark:bg-custom-500/20 text-custom-500 rounded-full size-8 flex items-center justify-center">
                        <Bot className="size-4" />
                      </div>
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div className={`flex flex-col ${message.isUser ? 'items-end' : 'items-start'}`}>
                    <div
                      className={`px-4 py-3 rounded-2xl shadow-sm text-sm ${message.isUser
                        ? "bg-custom-500 text-white rounded-br-sm"
                        : "bg-white dark:bg-zink-600 text-slate-700 dark:text-zink-100 border border-slate-200 dark:border-zink-500 rounded-bl-sm"
                        }`}
                    >
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.content) }} />
                      <div className={`text-[10px] mt-1 text-right opacity-70`}>
                        {formatDateTime(message.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <li className="flex justify-start">
                <div className="flex gap-3">
                  <div className="bg-custom-100 dark:bg-custom-500/20 text-custom-500 rounded-full size-8 flex items-center justify-center shrink-0 self-end">
                    <Bot className="size-4" />
                  </div>
                  <div className="bg-white dark:bg-zink-600 border border-slate-200 dark:border-zink-500 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex space-x-1 h-5 items-center">
                      <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </SimpleBar>
      </div>

      {/* Input Area - Only show in edit mode */}
      {(!isViewMode && statusCampaign !== 'Sent') && (
        <div className="p-4 bg-white dark:bg-zink-700 border-t border-slate-200 dark:border-zink-600">
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-zink-600/50 p-1.5 rounded-full border border-slate-200 dark:border-zink-500 focus-within:border-custom-500 dark:focus-within:border-custom-500 transition-colors">
            <input
              type="text"
              className="flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-sm px-4 text-slate-700 dark:text-zink-100 placeholder:text-slate-400 dark:placeholder:text-zink-300 w-full"
              placeholder="Type your ideas about campaigns..."
              value={currentMessage}
              onKeyDown={onKeyPress}
              onChange={(e) => setCurrentMessage(e.target.value)}
              disabled={isTyping}
              autoComplete="off"
            />
            <button
              type="button"
              className="flex items-center justify-center size-9 rounded-full bg-custom-500 text-white hover:bg-custom-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              onClick={addMessage}
              disabled={isTyping || currentMessage.trim() === ''}
            >
              <Send className="size-4" />
            </button>
          </div>
        </div>
      )}

      {/* View mode indicator */}
      {(isViewMode || statusCampaign === 'Sent') && (
        <div className="p-4 bg-gray-50 dark:bg-zink-600 border-t border-slate-200 dark:border-zink-600 text-center">
          <span className="text-sm text-gray-500 dark:text-zink-300">View Only Mode</span>
        </div>
      )}
    </div>
  );
}

export default CampaignAgent
